import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useHistory } from "react-router-dom";
import firebase, { CreateEmailCredential } from "../firebaseApp";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  tenantId: string;
  role: string;
  getToken(): Promise<string>;
}

export interface RegisterUserRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthContextProps {
  user?: AuthUser;
  onLogin?(email: string, password: string): void;
  onRegister?(request: RegisterUserRequest): void;
  onForgotPassword?(email: string): Promise<boolean>;
  onLogout?(): void;
  onRefreshToken?(): void;
  resetErrorMessage?(): void;
  changePassword?(password: string, confirmPassword: string): void;
  changeUserName?(userName: string): void;
  errorMessage?: string;
  isLoading: boolean;
  isStarting: boolean;
}

export const AuthDataContext = createContext<AuthContextProps>({
  isStarting: true,
  isLoading: false,
});

interface FirebaseException {
  code: string;
  message: string;
}

const getErrorMessage = (ex: FirebaseException) => {
  const msgMap: { [id: string]: string } = {
    "auth/wrong-password": "Senha inválida ou incorreta!",
    "auth/too-many-requests":
      "Defido a muitas tentativas esta conta foi temporariamente bloqueada. Você pode redefinir sua conta para acesso imediato ou tentar novamente mais tarde.",
  };

  console.log(ex);

  return msgMap[ex.code] ?? ex.message;
};

export const AuthDataProvider = (props: any) => {
  const [authData, setAuthData] = useState<AuthUser | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStarting, setIsStarting] = useState<boolean>(true);
  const history = useHistory();
  const getTenant = () => {
    return firebase.auth.currentUser?.getIdTokenResult(true).then((x) => {
      setAuthData({
        id: firebase.auth.currentUser!.uid,
        name: x.claims["name"] ?? firebase.auth.currentUser?.displayName,
        email: x.claims["email"],
        tenantId: x.claims["tenantId"],
        role: x.claims[
          "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
        ],
        getToken: async () => {
          return await firebase.auth.currentUser!.getIdToken();
        },
      });
      setIsStarting(false);
    });
  };

  useEffect(() => {
    firebase.auth.languageCode = "pt";

    firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        getTenant();
      } else {
        setAuthData(undefined);
        setIsStarting(false);
      }
    });
  }, []);

  const onLogout = useCallback(() => {
    firebase.auth.signOut();
    history.replace("/");
  }, [history]);

  const onLogin = (email: string, password: string) => {
    setIsLoading(true);
    firebase.auth
      .signInWithEmailAndPassword(email, password)
      .then((x) => {
        setErrorMessage(undefined);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(error.message);
      });
  };

  const onRefreshToken = useCallback(() => {
    getTenant();
    // setIsLoading(true);
    // setIsStarting(true);
    // getTenant()?.then(x => {
    //     setIsLoading(false);
    //     setIsStarting(true);
    // });
  }, []);

  const changePassword = (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    let email = firebase.auth.currentUser?.email ?? "";

    if (!currentPassword || currentPassword === "") {
      setErrorMessage("Senha atual inválida");
      setIsLoading(false);
      return;
    }

    const credential = CreateEmailCredential(email, currentPassword);

    firebase.auth.currentUser
      ?.reauthenticateWithCredential(credential)
      .then((reauthResp) => {
        firebase.auth.currentUser
          ?.updatePassword(newPassword)
          .then((uptPswResp) => {
            setErrorMessage(undefined);
            setIsLoading(false);
          })
          .catch((ex: FirebaseException) => {
            setErrorMessage(getErrorMessage(ex));
            setIsLoading(false);
          });
      })
      .catch((ex: FirebaseException) => {
        setErrorMessage(getErrorMessage(ex));
        setIsLoading(false);
      });
  };

  const changeUserName = (userName: string) => {
    setIsLoading(true);
    firebase.auth.currentUser
      ?.updateProfile({ displayName: userName })
      .then((reauthResp) => {
        setIsLoading(false);

        setAuthData((old: any) => ({ ...old, name: userName }));
      })
      .catch((ex: FirebaseException) => {
        setErrorMessage(getErrorMessage(ex));
        setIsLoading(false);
      });
  };

  const onRegister = (request: RegisterUserRequest) => {
    setIsLoading(true);
    firebase.auth
      .createUserWithEmailAndPassword(request.email, request.password)
      .then((x) => {
        if (x.user) {
          x.user?.updateProfile({ displayName: request.name }).then((_) => {
            setErrorMessage(undefined);
            setIsLoading(false);
          });
        } else {
          setErrorMessage(undefined);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(error.message);
      });
  };

  const onForgotPassword = (email: string) => {
    setIsLoading(true);
    return firebase.auth
      .sendPasswordResetEmail(email, {
        url: "https://apc-mobile-a329e.web.app/",
      })
      .then((x) => {
        setErrorMessage(undefined);
        setIsLoading(false);
        return true;
      })
      .catch((error) => {
        setIsLoading(false);
        setErrorMessage(error.message);
        return false;
      });
  };

  const resetErrorMessage = () => {
    setErrorMessage(undefined);
  };

  const authDataValue: AuthContextProps = useMemo(() => {
    return {
      user: authData,
      onLogin,
      onLogout,
      onRefreshToken,
      onRegister,
      onForgotPassword,
      changePassword,
      changeUserName,
      resetErrorMessage,
      errorMessage,
      isLoading,
      isStarting,
    };
  }, [authData, errorMessage, isLoading, isStarting, onRefreshToken, onLogout]);

  return <AuthDataContext.Provider value={authDataValue} {...props} />;
};

export const useAuthDataContext = (): AuthContextProps =>
  useContext(AuthDataContext);
